import React from 'react';
import { useBranding } from '../hooks/useBranding';

/**
 * Brand mark — shows uploaded logo_main if available, else the default "W + WillKart.ai" block.
 * Props:
 *   size: 'sm' | 'md' | 'lg' | 'xl' (icon/image height; defaults to 'md')
 *   showText: boolean — whether to render the "WillKart.ai" wordmark after the icon (fallback only)
 *   iconOnly: boolean — forces only the small square logo (uses logo_small)
 *   textSize: tailwind size class for wordmark (optional override, fallback only)
 */
const sizeMap = {
  sm: { box: 'w-8 h-8', letter: 'text-lg', wordmark: 'text-lg', img: 'h-9', imgMax: 'max-w-[180px]' },
  md: { box: 'w-10 h-10', letter: 'text-xl', wordmark: 'text-xl', img: 'h-12', imgMax: 'max-w-[240px]' },
  lg: { box: 'w-12 h-12', letter: 'text-2xl', wordmark: 'text-2xl', img: 'h-16', imgMax: 'max-w-[280px]' },
  xl: { box: 'w-16 h-16', letter: 'text-3xl', wordmark: 'text-3xl', img: 'h-20', imgMax: 'max-w-[340px]' },
};

export const BrandMark = ({ size = 'md', showText = true, iconOnly = false, textSize, className = '' }) => {
  const { logoMain, logoSmall } = useBranding();
  const dims = sizeMap[size] || sizeMap.md;
  const customLogo = iconOnly ? logoSmall : (logoMain || logoSmall);

  if (customLogo) {
    // When a custom logo is uploaded, render it as a plain image with size-aware height.
    // object-contain preserves aspect ratio — works equally well for wide wordmarks and square logos.
    return (
      <span className={`inline-flex items-center ${className}`} data-testid="brand-mark">
        <img
          src={customLogo}
          alt="WillKart"
          className={
            iconOnly
              ? `${dims.box} object-contain rounded-lg`
              : `${dims.img} ${dims.imgMax} object-contain`
          }
        />
      </span>
    );
  }

  // Default fallback: "W" square + WillKart.ai wordmark
  return (
    <span className={`inline-flex items-center gap-2 ${className}`} data-testid="brand-mark-default">
      <span
        className={`${dims.box} rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg flex-shrink-0`}
      >
        <span className={`text-slate-900 font-bold ${dims.letter}`}>W</span>
      </span>
      {showText && !iconOnly && (
        <span className={`${textSize || dims.wordmark} font-bold text-white tracking-tight`}>
          Will<span className="text-amber-400">Kart</span>
          <span className="text-slate-400 text-sm">.ai</span>
        </span>
      )}
    </span>
  );
};

export default BrandMark;
